import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Package, Gift } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { API } from "../constants/brand";
import { useCart, formatEUR } from "../context/CartContext";

export const Shop = () => {
  const [products, setProducts] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then(({ data }) => setProducts(data.products))
      .catch(() => toast.error("Produkte konnten nicht geladen werden."));
  }, []);

  const visible = products;

  return (
    <section id="shop" className="relative py-24 lg:py-32 bg-[#0d1320] noise-overlay">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Shop · Geschenkkarten</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-50">
              Geschenkkarten — <span className="text-gradient-blue">das perfekte Geschenk</span>
            </h2>
            <p className="mt-3 text-xs text-slate-500">
              Alle Preise verstehen sich inkl. 19 % MwSt. Geschenkkarten werden per E-Mail geliefert und bei jedem Werkstatt-Termin eingelöst.{" "}
              <a href="/widerruf" className="underline decoration-slate-600 underline-offset-2 hover:text-cyan-300 transition-colors">Widerrufsbelehrung</a>
            </p>
            <p className="mt-4 text-slate-400 max-w-xl text-base">
              Verschenke Werkstatt-Services: Wasserstoff-Motorreinigung oder Radwechsel &amp; Wuchten — wahlweise im Wert von
              80 € oder 120 €. Sicher online bezahlen und den Gutschein direkt per E-Mail erhalten.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => {
              const stock = p.stock ?? 0;
              const soldOut = stock <= 0;
              return (
                <motion.article
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 32 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className={`group rounded-3xl overflow-hidden bg-[#121826] border transition-colors duration-500 flex flex-col ${
                    soldOut ? "border-white/5" : "border-white/8 hover:border-cyan-400/30"
                  }`}
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={p.name}
                        loading="lazy"
                        className={`w-full h-full object-cover transition-transform duration-700 ${
                          soldOut ? "grayscale opacity-40" : "group-hover:scale-110"
                        }`}
                      />
                    ) : (
                      <div className="relative w-full h-full bg-gradient-to-br from-[#101c30] via-[#0d1626] to-[#0B0F17] flex flex-col items-center justify-center gap-3 p-6">
                        <div
                          className="absolute inset-0 opacity-30 transition-opacity duration-700 group-hover:opacity-50"
                          style={{ background: "radial-gradient(circle at 30% 20%, rgba(41,182,246,0.35) 0%, transparent 55%)" }}
                        />
                        <Gift size={38} className="relative text-cyan-300" strokeWidth={1.5} />
                        <div className="relative font-mono-tech text-4xl font-extrabold text-gradient-blue">{formatEUR(p.price_eur)}</div>
                        <div className="relative text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono-tech">M-Line Solutions</div>
                        <div className="relative text-[10px] uppercase tracking-[0.25em] text-cyan-400/70 font-mono-tech border border-cyan-400/25 rounded-full px-3 py-1">Geschenkkarte</div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121826] via-transparent to-transparent" />
                    <span
                      className={`absolute top-4 left-4 rounded-full backdrop-blur px-3 py-1 text-[10px] font-mono-tech uppercase tracking-[0.2em] border ${
                        soldOut
                          ? "bg-red-950/80 text-red-300 border-red-400/30"
                          : "bg-[#0B0F17]/80 text-cyan-300 border-cyan-400/25"
                      }`}
                      data-testid={`product-stock-badge-${p.id}`}
                    >
                      {soldOut ? "Ausverkauft" : stock <= 3 ? `Nur noch ${stock} auf Lager` : p.tag}
                    </span>
                  </div>
                  <div className="p-5 flex flex-col gap-4 flex-1">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono-tech">{p.category}</div>
                      <h3 className="text-lg font-bold text-slate-50 mt-1 leading-snug">{p.name}</h3>
                      <p className="text-sm text-slate-400 mt-2 leading-relaxed flex items-start gap-2">
                        <Package size={14} className="text-cyan-400 shrink-0 mt-0.5" />
                        {p.desc}
                      </p>
                    </div>
                    <div className="mt-auto pt-2 flex items-center justify-between gap-3">
                      <div className="font-mono-tech text-xl font-bold text-gradient-blue">{formatEUR(p.price_eur)}</div>
                      <button
                        data-testid={`product-add-to-cart-${p.id}`}
                        disabled={soldOut}
                        onClick={() => {
                          add(p);
                          toast.success(`${p.name} zum Warenkorb hinzugefügt.`);
                        }}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold transition-transform duration-300 ${
                          soldOut
                            ? "bg-white/5 text-slate-600 cursor-not-allowed border border-white/10"
                            : "bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] text-white hover:scale-105 active:scale-95"
                        }`}
                      >
                        <ShoppingCart size={14} />
                        {soldOut ? "Ausverkauft" : "In den Warenkorb"}
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
